import { useState, useEffect } from "react";
import { useController } from "react-hook-form";

const usePermissionOverrides = (control, name, defaultValue) => {
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: defaultValue ?? [],
  });

  // value format: [{id: 5, type: "DENY"}, {id: 6, type: "ALLOW"}]
  
  const getPermissionState = (permissionId) => {
    const override = value?.find(v => v.id === permissionId);
    return override ? override.type : "DEFAULT";
  };

  const handlePermissionChange = (permissionId, newType) => {
    let newValue = [...(value || [])];
    
    if (newType === "DEFAULT") {
      // Remove the override
      newValue = newValue.filter(v => v.id !== permissionId);
    } else {
      // Add or update the override
      const existingIndex = newValue.findIndex(v => v.id === permissionId);
      if (existingIndex >= 0) {
        newValue[existingIndex] = { id: permissionId, type: newType };
      } else {
        newValue.push({ id: permissionId, type: newType });
      }
    }
    
    onChange(newValue);
  };

  const handleParentChange = (parent, children, newType) => {
    let newValue = [...(value || [])];
    const allIds = children.map(c => c.value);
    
    if (newType === "DEFAULT") {
      // Remove all children overrides
      newValue = newValue.filter(v => !allIds.includes(v.id));
    } else {
      // Set all children to the same type
      allIds.forEach(id => {
        const existingIndex = newValue.findIndex(v => v.id === id);
        if (existingIndex >= 0) {
          newValue[existingIndex] = { id, type: newType };
        } else {
          newValue.push({ id, type: newType });
        }
      });
    }
    
    onChange(newValue);
  };

  const getParentState = (children) => {
    const childIds = children.map(child => child.value);
    const childStates = childIds.map(id => getPermissionState(id));
    
    const allDefault = childStates.every(state => state === "DEFAULT");
    const allAllow = childStates.every(state => state === "ALLOW");
    const allDeny = childStates.every(state => state === "DENY");
    
    if (allDefault) return "DEFAULT";
    if (allAllow) return "ALLOW";
    if (allDeny) return "DENY";
    return "MIXED"; // Mixed state
  };

  return {
    value,
    ref,
    error,
    getPermissionState,
    handlePermissionChange,
    handleParentChange,
    getParentState,
  };
};

export default usePermissionOverrides;
